import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * DotGridBackground — A lightweight, GPU-friendly dot grid overlay.
 * 
 * Drop into any page that has `position: 'relative'` on its container.
 * The layers are clipped to that container, so the glow never widens the page.
 * Fully static CSS (zero animation, zero GPU cost).
 *
 * Props:
 *  - dotColor   : CSS color for the dots (default: theme slate-300)
 *  - dotSize    : diameter of each dot in px (default: 1)
 *  - spacing    : grid gap in px (default: 28)
 *  - opacity    : overall opacity 0-1 (default: 0.5)
 *  - fade       : show edge fade masks (default: true)
 *  - glow       : show the subtle center glow (default: true)
 *  - glowColor  : CSS color for the center glow (default: theme mainColor-400)
 *  - glowTop    : vertical position of glow center (default: '30%')
 *  - zIndex     : z-index for the layers (default: 1)
 */
const DotGridBackground = ({
  dotColor,
  dotSize = 1,
  spacing = 28,
  opacity = 0.5,
  fade = true,
  glow = true,
  glowColor,
  glowTop = '30%',
  zIndex = 1,
}) => {
  const theme = useTheme();
  const resolvedDotColor = dotColor || theme.modern.colors.modern.slate[300];
  const resolvedGlowColor = glowColor || theme.modern.colors.modern.mainColor[400];

  return (
    <Box
      aria-hidden="true"
      sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex, pointerEvents: 'none' }}
    >
      {/* Dot grid layer */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex,
          pointerEvents: 'none',
          backgroundImage: `radial-gradient(circle, ${resolvedDotColor} ${dotSize}px, transparent ${dotSize}px)`,
          backgroundSize: `${spacing}px ${spacing}px`,
          opacity,
          ...(fade && {
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 85%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 85%, transparent 100%)',
          }),
        }}
      />

      {/* Subtle radial glow */}
      {glow && (
        <Box
          sx={{
            position: 'absolute',
            top: glowTop,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '800px',
            height: '600px',
            zIndex,
            pointerEvents: 'none',
            background: `radial-gradient(ellipse at center, ${resolvedGlowColor}12 0%, transparent 70%)`,
            filter: 'blur(40px)',
          }}
        />
      )}
    </Box>
  );
};

export default DotGridBackground;
